import {
  Tabs as BaseTabs,
  type TabContentProps,
  type TabListProps,
  type TabTriggerProps,
  type TabsRootProps,
} from "@ark-ui/react";
import clsx from "clsx";
import * as React from "react";

interface TabsProps extends Omit<TabsRootProps, "orientation"> {
  variant?: "underline" | "rounded" | "rounded-filled" | "outline" | "group";
  size?: "sm" | "md" | "lg";
}

interface TabsTriggerProps extends TabTriggerProps {
  icon?: React.ReactElement;
}

const Tabs = React.forwardRef<HTMLDivElement, TabsProps>((props, ref) => {
  const { className, variant = "rounded", size = "md", ...rest } = props;
  return (
    <BaseTabs.Root
      ref={ref}
      data-variant={variant}
      data-size={size}
      className={clsx("ox-reset ox-TabsRoot", className)}
      {...rest}
    />
  );
});

const TabsList = React.forwardRef<HTMLDivElement, TabListProps>(
  (props, ref) => {
    const { className, ...rest } = props;
    return (
      <BaseTabs.List
        ref={ref}
        className={clsx("ox-reset ox-TabsList", className)}
        {...rest}
      />
    );
  },
);

const TabsTrigger = React.forwardRef<HTMLButtonElement, TabsTriggerProps>(
  (props, ref) => {
    const { className, icon, children, ...rest } = props;
    return (
      <BaseTabs.Trigger
        ref={ref}
        className={clsx("ox-reset ox-TabsTrigger", className)}
        {...rest}
      >
        {icon ? (
          <span className="ox-Reset ox-TabsTriggerIcon">{icon}</span>
        ) : null}
        {children}
      </BaseTabs.Trigger>
    );
  },
);

const TabsContent = React.forwardRef<HTMLDivElement, TabContentProps>(
  (props, ref) => {
    const { className, ...rest } = props;
    return (
      <BaseTabs.Content
        ref={ref}
        className={clsx("ox-reset ox-TabsContent", className)}
        {...rest}
      />
    );
  },
);
Tabs.displayName = "Tabs";
TabsList.displayName = "TabsList";
TabsTrigger.displayName = "TabsTrigger";
TabsContent.displayName = "TabsContent";
export { Tabs, TabsList, TabsTrigger, TabsContent };
export type { TabsProps };
